import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Autocomplete, Box, createFilterOptions, Divider, TextField, } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useListOpponentTeams } from "../api/MatchApiProvider";
import { useRetrievePlayersByTeamId } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import { FormActions } from "../utils/FormActions";
import { TeamChooser } from "./TeamChooser";
export const ExternalMatchForm = ({ team, onSubmit, externalMatch, }) => {
    const useFormProps = useForm({
        defaultValues: {
            kickOff: externalMatch?.kickOff,
            players: externalMatch?.players?.map((p) => p.id) || [],
            opponentTeamId: externalMatch?.opponentTeam?.id,
            opponentTeamName: undefined,
            team: team.id,
        },
    });
    const { control, handleSubmit, formState: { errors }, setValue, } = useFormProps;
    const { data: players, error: playersError, isLoading: playersIsLoading, } = useRetrievePlayersByTeamId({ teamId: team.id });
    const { data: opponentTeams, isLoading: opponentTeamsIsLoading, error: opponentTeamsError, } = useListOpponentTeams();
    const [teamSelection, _setTeamSelection] = React.useState({
        teams: [],
        bench: [],
    });
    const setTeamSelection = (newSelection) => {
        _setTeamSelection(newSelection);
    };
    // A player dragged to the bench who has left the team: pending a confirmation
    // before we drop them, since they can't be re-added once the match is saved.
    const [pendingRemoval, setPendingRemoval] = React.useState(null);
    // The name is held separately so it survives the dialog's close animation —
    // clearing pendingRemoval on cancel must not flip the text to a fallback.
    const [removalName, setRemovalName] = React.useState("");
    const requestRemoval = (player) => {
        setRemovalName(player.name ?? "This player");
        setPendingRemoval(player);
    };
    // Keep the submitted lineup in sync with the Team column. That column includes
    // both roster players and anyone who has left the team (kept unless the user
    // removes them), so editing a match never silently drops a departed player.
    React.useEffect(() => {
        const ids = teamSelection.teams[0]?.players.map((p) => p.id) ?? [];
        setValue("players", ids);
    }, [teamSelection, setValue]);
    const confirmRemoveDeparted = () => {
        if (pendingRemoval?.id != null) {
            const removedId = pendingRemoval.id;
            _setTeamSelection((prev) => ({
                ...prev,
                teams: prev.teams.map((t) => ({
                    ...t,
                    players: t.players.filter((p) => p.id !== removedId),
                })),
            }));
        }
        setPendingRemoval(null);
    };
    const [selectedOpponentTeam, setSelectedOpponentTeam] = React.useState(externalMatch?.opponentTeam
        ? {
            opponentTeamId: externalMatch.opponentTeam.id,
            opponentTeamName: externalMatch.opponentTeam.name,
        }
        : null);
    useEffect(() => {
        const { opponentTeamId, opponentTeamName } = selectedOpponentTeam || {};
        if (opponentTeamId) {
            setValue("opponentTeamId", opponentTeamId);
            setValue("opponentTeamName", null);
        }
        else {
            setValue("opponentTeamId", null);
            setValue("opponentTeamName", opponentTeamName);
        }
    }, [selectedOpponentTeam]);
    React.useEffect(() => {
        if (!players)
            return;
        const getRosterPlayerById = (id) => players.find((p) => p.id === id);
        const matchPlayerIds = new Set(externalMatch?.players?.map((p) => p.id));
        setTeamSelection({
            teams: [
                {
                    name: "Team",
                    // Everyone who was in the match: roster members as full players, and
                    // anyone who has since left the team as a summary (rendered greyed
                    // with a warning pill, removable only via a confirmation).
                    players: (externalMatch?.players || []).map((mp) => getRosterPlayerById(mp.id) ?? mp),
                    primaryColour: undefined,
                },
            ],
            bench: players.filter((p) => p.isActive !== false && !matchPlayerIds.has(p.id)),
        });
    }, [players, externalMatch]);
    const isLoading = playersIsLoading || opponentTeamsIsLoading;
    const filter = createFilterOptions();
    if (isLoading) {
        return _jsx(Loading, {});
    }
    if (!team || !opponentTeams || !players) {
        return (_jsx(ErrorView, { error: new Error("Team, opponent teams or players not found") }));
    }
    if (opponentTeamsError || playersError) {
        return _jsx(ErrorView, { error: opponentTeamsError || playersError });
    }
    return (_jsx(FormProvider, { ...useFormProps, children: _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, sx: { mt: 1 }, children: [_jsx(Autocomplete, { fullWidth: true, value: selectedOpponentTeam, onChange: (event, newValue) => {
                        if (typeof newValue === "string") {
                            setSelectedOpponentTeam({
                                opponentTeamName: newValue,
                                opponentTeamId: null,
                            });
                        }
                        else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            setSelectedOpponentTeam({
                                opponentTeamName: newValue.inputValue,
                                opponentTeamId: null,
                            });
                        }
                        else {
                            setSelectedOpponentTeam(newValue);
                        }
                    }, filterOptions: (options, params) => {
                        const filtered = filter(options, params);
                        const { inputValue } = params;
                        const isExisting = options.some((option) => inputValue === option.opponentTeamName);
                        if (inputValue !== "" && !isExisting) {
                            filtered.push({
                                inputValue,
                                opponentTeamName: `Add "${inputValue}"`,
                            });
                        }
                        return filtered;
                    }, selectOnFocus: true, clearOnBlur: true, handleHomeEndKeys: true, id: "free-solo-with-text-demo", options: (opponentTeams.map((opponentTeam) => {
                        return {
                            opponentTeamId: opponentTeam.id,
                            opponentTeamName: opponentTeam.name,
                        };
                    }) || []), getOptionLabel: (option) => {
                        if (typeof option === "string") {
                            return option;
                        }
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        return option.opponentTeamName;
                    }, renderOption: (props, option) => {
                        return _jsx("li", { ...props, children: option.opponentTeamName });
                    }, freeSolo: true, renderInput: (params) => (_jsx(TextField, { ...params, margin: "normal", fullWidth: true, label: "Opponent Team", required: true, error: !!errors.opponentTeamId || !!errors.opponentTeamName, helperText: errors.opponentTeamId?.message ||
                            errors.opponentTeamName?.message, autoFocus: true })) }), _jsx(Controller, { name: "kickOff", control: control, rules: { required: "Kick-Off is required" }, render: ({ field }) => (_jsx(DateTimePicker, { label: "Kick-Off", ...field, value: field.value ? dayjs(field.value) : null, onChange: (newValue) => field.onChange(newValue), sx: { width: "100%", mt: 2 }, slotProps: {
                            textField: {
                                error: !!errors.kickOff,
                                helperText: errors.kickOff ? errors.kickOff.message : "",
                            },
                        } })) }), _jsx(Divider, { sx: {
                        margin: "20px",
                    } }), _jsx(TeamChooser, { teamSelection: teamSelection, setTeamSelection: setTeamSelection, canEditTeamNames: false, maxTeams: 1, minTeams: 1, onRemoveDeparted: requestRemoval }), _jsx(DeleteDialog, { open: pendingRemoval !== null, title: `Remove ${removalName || "player"}?`, description: `${removalName} has left the team, so they can't be added back once removed from this match.`, confirmText: "Remove", onCancel: () => setPendingRemoval(null), onConfirm: confirmRemoveDeparted }), _jsx(FormActions, { submitLabel: externalMatch === undefined ? "Create" : "Update" })] }) }));
};
